/**
 * HR help text for Master Data.
 *
 * Master data is the list of choices the rest of the system offers. A record
 * here does nothing on its own, but it appears in dropdowns across employee,
 * leave, payroll, and attendance screens. Deactivating one hides it from future
 * selections while leaving existing records untouched, which is almost always
 * safer than deleting it.
 */

// Every lookup list (blood groups, religions, cities, skills, and so on).
const lookup = {
  name: "Name employees and HR will see wherever this list appears in a dropdown. Use the wording your team already says out loud.",
  shortname: "Short form used where the full name does not fit, such as tables and exported reports.",
  short_title: "Short form used where the full title does not fit, such as tables and exported reports.",
  code: "Unique code identifying this record in imports, exports, and integrations. Keep it stable once data is using it.",
  title: "Title employees will see for this entry.",
  description: "Internal note explaining when this option should be picked. Employees do not see it.",
  type: "Category this record falls under. It groups related entries in lists and reports.",
  type_id: "Category this record falls under.",
  value: "The stored value behind this option. Change it only if you know what already refers to it.",
  is_active: "Only active entries appear in dropdowns on new records. Turning this off hides it going forward without affecting employees who already have it.",
  selected: "Marks this as the option pre-selected for HR when a new record is created.",
  parent_id: "The entry this one sits under, which builds the list into a hierarchy.",
  country_id: "Country this entry belongs to.",
  state_id: "State or province this entry belongs to.",
  city_id: "City this entry belongs to.",
  location_id: "Location this entry belongs to.",
  branch_id: "Branch this entry belongs to.",
  employment_id: "Employee this entry relates to.",
  employment_ids: "Employees this entry relates to.",
  employee_id: "Employee this entry relates to.",
  weightage: "How much this item counts towards the total score. The weightages across all items should add up to what your scoring expects.",
  group: "Groups this entry with related ones in lists and reports.",
  category: "Category this entry falls under.",
  expiry_date: "Date this entry stops being valid. It is hidden from employees from this date onwards.",
  date: "Date recorded against this entry.",
  attachment: "Supporting file kept with this record.",
  file_attachment: "File employees will be able to open from this entry.",
  file_cover: "Cover image shown for this entry in lists and on the dashboard. A wide image works best.",
  status: "Current state of this entry, which decides where it appears and who is expected to act on it.",
  created_by_name: "Who created this entry.",
  verifiedby_id: "Who checked and confirmed this entry.",
  id: "System identifier for this record.",
  identification_number: "Serial or identification number printed on this item, used to tell one unit from another.",
  asset_type_id: "Category this asset belongs to, such as laptop or vehicle.",
  manufacturer: "Who makes this item.",
  model: "Model of this item.",
  acquired_date: "Date the company acquired this item. Depreciation and warranty reporting count from here.",
  current_value: "What this item is worth today, after depreciation.",
  accolade_date: "Date this award was given.",
  assignby_id: "Who assigned this entry.",
  assignto_id: "Who this entry is assigned to.",
  assigned_at: "When this entry was assigned.",
  base: "The currency all other rates are expressed against. Only one currency should be the base.",
  conversion: "How many units of this currency equal one unit of the base currency. Amounts entered in this currency are converted with it.",
  display_symbol: "Symbol shown next to amounts in this currency on screen, such as Rs.",
  intl_symbol: "International three-letter code for this currency, such as PKR, used on exports and bank files.",
  is_expiry: "Turn this on when documents of this type have an expiry date, so HR is asked for one and can be warned before it lapses.",
  is_required: "When ticked, employees must provide this before their record can be completed.",
  is_multi_attachments: "Allow more than one file to be attached for this type.",
  is_additional_info: "Ask for extra detail alongside this entry.",
  is_experience_required: "Only offer this option to employees who meet the experience requirement.",
  is_group: "Treat this as a group that other entries can be placed under.",
  is_new: "Marks this entry as newly added, so it stands out in the list.",
  is_taxable: "Include amounts of this type in taxable income when payroll calculates tax.",
  payitem_id: "Payitem amounts of this type are paid or deducted through. It decides how they appear on the payslip.",
  kpi_ids: "KPIs included in this allocation. Each one is scored separately.",
  recursion_type: "How often this repeats: daily, weekly, or monthly.",
  custom_date: "The exact date used instead of a repeating pattern.",
  checked_by_name: "Who verified this entry.",
};

const document = {
  name: "Name of this document so HR can recognize it in the list.",
  title: "Title employees will see for this document.",
  document_type_id: "What kind of document this is. It decides where the document appears on the employee's file.",
  expiry_date: "Date the document lapses, so HR can chase a renewal before it does.",
  file_attachment: "The document file itself.",
  file_cover: "Cover image shown for this document in lists.",
  description: "Internal note about this document.",
};

const announcement = {
  title: "Headline employees will see on their dashboard. Keep it short and specific.",
  date: "Date the announcement is published from.",
  wow_date: "Date this achievement is being recognized for.",
  expiry_date: "Date the announcement stops being shown. Leaving it empty keeps it on the dashboard indefinitely.",
  employment_id: "Employee being recognized.",
  file_attachment: "File employees can open from this announcement.",
  file_cover: "Cover image shown with the announcement on the dashboard. A wide image works best.",
  description: "The full message employees will read.",
};

const machine = {
  name: "Name of this attendance machine, such as Main Gate. It identifies the machine in punch logs and imports.",
  serial_number: "Serial number printed on the device. It is how the system recognizes a device that connects on its own.",
  ip: "The machine's address on the office network. Ask IT for it; a wrong address means punches are never pulled.",
  udp_port: "Network port used to talk to the machine. Keep the value IT provides unless they say otherwise.",
  soap_port: "Second network port used by some machine models. Keep the value IT provides.",
  com_key: "Communication key set on the device. It must match the device exactly or the connection is refused.",
  mac: "The machine's hardware address, used to identify it on the network.",
  machine_type_id: "Model family of this device. It decides how the system talks to it, so an incorrect type stops punches being read.",
  branch_id: "Branch this machine is installed at. Punches from it are attributed to this branch.",
  location_id: "Location this machine is installed at.",
  is_auto_import: "Pull punches from this machine automatically instead of importing them by hand.",
  protocol: "How the system communicates with this device. Keep the value IT provides.",
  manufacturer: "Who makes this device.",
  model: "Model of this device.",
  last_activity_at: "When this machine last reported in. A long gap usually means it is offline.",
  is_active: "Only active machines are contacted for punches. Turn it off for a device that has been removed.",
  command: "Instruction sent to the device. Use it only with guidance from IT.",
  pin: "The employee number as it is stored on the device. It must match the employee code, otherwise punches are not matched to anyone.",
  card: "Card number registered against the employee on this device.",
  password: "Device password for this employee, where the machine uses one.",
  privilege: "What this user may do on the device itself, such as ordinary user or device administrator. Device administrators can change the machine's settings.",
  group: "Group on the device this user belongs to, which controls when they are allowed to punch.",
  tz: "Time zone rule on the device that decides the hours this user may punch in.",
  category: "Category recorded against this device entry.",
  employee_id: "Employee this device record belongs to.",
  employment_ids: "Employees to register on this machine. Only registered employees can punch on it.",
  user_id: "The employee's identifier on the device. It must match the employee code, otherwise punches are not matched to anyone.",
};

const scheduler = {
  name: "Name of this scheduled job so HR can recognize it in the list.",
  type: "Which job runs on this schedule, such as attendance calculation or quota allotment.",
  is_enabled: "Only an enabled schedule runs. Turning it off stops future runs without deleting the schedule.",
  one_time_date: "Date this job runs, for a schedule that runs only once.",
  one_time_time: "Time this job runs, for a schedule that runs only once.",
  occur_once_time: "Time of day this job runs on each scheduled day.",
  occur_every_start_time: "Earliest time of day this job may run when it repeats through the day.",
  occur_every_end_time: "Latest time of day this job may run when it repeats through the day.",
  occur_every_number: "How often the job repeats within the day, counted in the unit chosen next to it.",
  occur_every_type: "The unit the repeat interval is counted in, such as hours or minutes.",
  recur_duration_start_date: "First date this schedule is active from. Nothing runs before it.",
  recur_duration_end_date: "Last date this schedule stays active. Leaving it empty keeps the job running indefinitely.",
  recurr_type: "How often the job repeats: daily, weekly, or monthly.",
  recursion_type: "How often the job repeats: daily, weekly, or monthly.",
  recurr_frequency: "How many days, weeks, or months apart the runs are.",
  is_once: "Run this job a single time rather than on a repeating schedule.",
  cron: "Advanced timing expression, for schedules that do not fit the options above. Ask IT if you are unsure of the format.",
  description: "Internal note explaining what this schedule is for.",
};

const requestFlow = {
  type: "Which kind of request this approval flow applies to, such as leave or attendance.",
  node_type_id: "What this step is: an approver, a consent, or the owner of the request.",
  request_as_id: "The role the approver acts in at this step, such as line manager or head of department. The actual person is worked out per employee from that role.",
  employment_id: "A specific employee who approves at this step, used instead of a role.",
  employee_id: "A specific employee who approves at this step.",
  next: "The step the request moves to after this one is approved.",
  is_first: "Marks this as the first step, where every new request enters the flow.",
  is_last: "Marks this as the final step. Once it is approved, the request is complete and its effect is applied.",
  has_condition: "Send the request down one of two paths depending on a condition, instead of always to the same next step.",
  expression: "The condition checked when the request reaches this step. It decides which of the two paths below is taken, so a wrong condition sends requests to the wrong approver.",
  if_true: "Step the request moves to when the condition is met.",
  if_false: "Step the request moves to when the condition is not met.",
  editable: "Allow the approver at this step to change the request's values, not only approve or reject it.",
  type_id: "Which kind of request this approval flow applies to, such as leave or attendance.",
  id: "System identifier for this step.",
};

const role = {
  name: "Name of this role, such as HR Manager. It is assigned to employees and decides what they can see and do.",
  privilege: "What holders of this role may do on each screen. Removing a privilege hides the action for everyone with this role the next time they log in.",
  ui_sref: "The screen this menu entry opens. Change it only with guidance from IT.",
  parent_id: "The menu this entry sits under.",
  description: "Internal note explaining who this role is meant for.",
  is_active: "Only active roles can be assigned to employees.",
};

const code = {
  key: "Which record type this numbering applies to, such as employees or leave requests.",
  name: "Name of this numbering rule.",
  value: "The prefix put in front of the generated number, such as EMP.",
  count: "The next number to be issued. Changing it changes where the numbering carries on from, so avoid moving it backwards onto numbers already used.",
  padding: "How many digits the number is padded to, so 5 with a padding of 4 becomes 0005. It keeps codes the same width in lists and exports.",
};

const kpi = {
  title: "Name of this KPI as it appears on the evaluation form.",
  short_title: "Short form used where the full title does not fit, such as tables and exported reports.",
  description: "Explains to reviewers what this KPI measures, so different people score it consistently.",
  weightage: "How much this KPI counts towards the overall score.",
  qa_ids: "Employees who verify the results recorded against this KPI.",
  qa_remarks: "Remarks left by the person who verified this entry.",
};

const todo = {
  title: "Short name for this task so the assignee knows what is being asked.",
  type: "What kind of task this is.",
  protocol: "The procedure to follow for this task.",
  assignee_id: "Employee responsible for completing this task.",
  status: "Current state of the task, which decides whose list it appears on.",
  qa_ids: "Employees who check the task once it is done.",
  qa_remarks: "Remarks left by the person who checked the task.",
  assignee_remarks: "Remarks left by the person who did the task.",
  reason: "Why the task was rejected or reopened.",
  description: "What the assignee needs to do.",
  assigned_at: "When this task was assigned to the employee.",
  created_by_name: "Who created this task.",
  checked_by_name: "Who verified the completed task.",
  weightage: "How much this task counts towards the overall score.",
};

export const masterTooltips = {
  lookup,
  document,
  announcement,
  machine,
  scheduler,
  requestFlow,
  role,
  code,
  kpi,
  todo,
};

export default masterTooltips;
